import moment from 'moment';
import { Button, Label, Modal, Spinner, Textarea, TextInput } from 'flowbite-react';
import React, { FormEvent, useEffect, useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { GradeType, UpdateEducationalDetailsInput, UserEducationalDetail } from 'types';
import { useMutation, useQueryClient } from 'react-query';
import { createUserEducationFn, editUserEducationFn } from 'src/apis/profile';
import Select from 'react-select';
import { degreeTypeOptions, gradeTypeOptions } from 'src/utils/educationOptions';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	educationDetails: UserEducationalDetail;
	isEditing?: boolean;
	selectedEducationId: string;
};

const EditProfileEducationModal = ({ isOpen, selectedEducationId, onClose, isEditing, educationDetails }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [educationId, setEducationId] = useState('');
	const [dateError, setDateError] = useState<string>('');
	const [educationalDetails, setEducationalDetails] = useState<UpdateEducationalDetailsInput>({
		updateInfo: {
			collegeName: '',
			degreeName: '',
			description: '',
			fieldOfStudy: '',
			gradeType: '',
			gradeValue: 0,
			endedAt: new Date(),
			collagelocation: '',
			startedFrom: new Date(),
		},
	});

	const editEducationDetailsFn = useMutation({
		mutationKey: 'editEducationDetail',
		mutationFn: editUserEducationFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Education detail updated successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
		},
	});

	const createEducationDetailsFn = useMutation({
		mutationKey: 'addEducationDetail',
		mutationFn: createUserEducationFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			window.scrollTo({ top: 0, behavior: 'smooth' });
			toast({
				title: 'Education detail added successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
		},
	});

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (educationalDetails.updateInfo.endedAt && educationalDetails.updateInfo.startedFrom) {
			if (educationalDetails.updateInfo.startedFrom >= educationalDetails.updateInfo.endedAt) {
				setDateError('start date must be less than end date');
				return;
			}
		}

		if (!moment(educationalDetails.updateInfo.startedFrom).isValid()) {
			setDateError('Invalid start date');
			return;
		}

		if (isEditing) {
			editEducationDetailsFn.mutate({ id: educationId, data: educationalDetails });
		} else {
			createEducationDetailsFn.mutate(educationalDetails);
		}
	};

	useEffect(() => {
		setEducationalDetails({
			updateInfo: {
				collegeName: educationDetails.collegeName || '',
				degreeName: educationDetails.degreeName || '',
				fieldOfStudy: educationDetails.fieldOfStudy || '',
				gradeType: educationDetails.gradeType || '',
				gradeValue: educationDetails.gradeValue || 0,
				endedAt: moment(educationDetails.endedAt).toDate(),
				collagelocation: educationDetails.collagelocation || '',
				startedFrom: moment(educationDetails.startedFrom).toDate(),
			},
		});
		setEducationId(selectedEducationId || '');
	}, [educationDetails, selectedEducationId]);

	useEffect(() => {
		setDateError('');
	}, [isOpen]);

	const selectStyles = {
		control: (provided: any) => ({
			...provided,
			backgroundColor: '#4B5563',
			border: '1px solid #6B7280',
		}),
		singleValue: (provided: any) => ({
			...provided,
			color: 'white',
		}),
		menuList: (provided: any) => ({
			...provided,
			backgroundColor: '#4B5563',
		}),
		option: (provided: any, state: any) => ({
			...provided,
			backgroundColor: state.isFocused ? '#CCDDFF4D' : '#4B5563',
			cursor: 'pointer',
		}),
	};

	return (
		<Modal dismissible={true} show={isOpen} onClose={onClose} size="lg" className="!h-full backdrop-blur-[4px]">
			<Modal.Header className="rounded-t-md border-b-0 dark:bg-[#1F2A37]">
				<div>
					<p className="font-inter text-[20px] font-[700] dark:text-white">Education</p>
				</div>
			</Modal.Header>
			<form className="dark:text-white" onSubmit={handleSubmit}>
				<Modal.Body className="dark:bg-[#1F2A37]">
					<div className="">
						{/* Degree */}
						<div className="mb-[5px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="edcuation_title"
									value="Degree"
								/>
								<span className="text-red-600">*</span>
							</div>
							<Select
								styles={selectStyles}
								menuPlacement={'auto'}
								options={degreeTypeOptions}
								classNamePrefix="my-select-plans"
								value={degreeTypeOptions.find(option => option.value === educationalDetails.updateInfo.degreeName)}
								onChange={e =>
									setEducationalDetails({
										updateInfo: {
											...educationalDetails.updateInfo,
											degreeName: e?.value,
										},
									})
								}
							/>
						</div>
						{/* FieldOfStudy */}
						<div className="mb-[5px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="fieldOfStudy"
									value="Field of Study"
								/>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								placeholder="Information technology"
								type="text"
								id="fieldOfStudy"
								name="fieldOfStudy"
								value={educationalDetails.updateInfo.fieldOfStudy || ''}
								onChange={e =>
									setEducationalDetails({
										updateInfo: {
											...educationalDetails.updateInfo,
											fieldOfStudy: e.target.value,
										},
									})
								}
							/>
						</div>
						{/* InstitutionName */}
						<div className="mb-[5px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="title"
									value="School/Institution Name"
								/>
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								placeholder="Amity University"
								type="text"
								id="title"
								name="title"
								value={educationalDetails.updateInfo.collegeName || ''}
								onChange={e =>
									setEducationalDetails({
										updateInfo: {
											...educationalDetails.updateInfo,
											collegeName: e.target.value,
										},
									})
								}
							/>
						</div>
						{/* Location */}
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="location"
									value="Location"
								/>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="location"
								name="Location"
								placeholder="Bengaluru"
								value={educationalDetails.updateInfo.collagelocation || ''}
								onChange={e =>
									setEducationalDetails({
										updateInfo: {
											...educationalDetails.updateInfo,
											collagelocation: e.target.value,
										},
									})
								}
							/>
						</div>
						{/* Score */}
						<div className="mb-[5px] flex gap-4">
							<div className="mb-[5px] basis-1/2">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="GradeType"
										value="Grade Type"
									/>
								</div>
								<Select
									isClearable={true}
									styles={selectStyles}
									menuPlacement={'auto'}
									options={gradeTypeOptions}
									classNamePrefix="my-select-plans"
									value={gradeTypeOptions.find(option => option.value === educationalDetails.updateInfo.gradeType)}
									onChange={e =>
										setEducationalDetails({
											updateInfo: {
												...educationalDetails.updateInfo,
												gradeType: e?.value,
											},
										})
									}
								/>
							</div>
							<div className="mb-[5px] basis-1/2">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="gradeValue"
										value="Grade value"
									/>
								</div>
								<TextInput
									style={{
										fontFamily: "'Inter', sans-serif",
									}}
									type="number"
									id="gradeValue"
									name="gradeValue"
									min="0"
									max={educationalDetails.updateInfo.gradeType === GradeType.Percentage ? '100' : '10'}
									value={educationalDetails.updateInfo.gradeValue}
									onChange={e =>
										setEducationalDetails({
											updateInfo: {
												...educationalDetails.updateInfo,
												gradeValue: Number(e.target.value),
											},
										})
									}
								/>
							</div>
						</div>
						{/* Description */}
						<div className="mb-[8px] hidden">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="description"
									value="Description"
								/>
							</div>
							<Textarea
								style={{
									fontFamily: "'Inter', sans-serif",
									height: '90px',
								}}
								id="description"
								name="description"
								value={educationalDetails.updateInfo.description || ''}
								onChange={e =>
									setEducationalDetails({
										updateInfo: {
											...educationalDetails.updateInfo,
											description: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[5px] flex gap-4">
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="startedFrom"
										value="Start date"
									/>
									<span className="text-red-600">*</span>
								</div>
								<div className="flex gap-[16px]">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="startedFrom"
										name="startedFrom"
										value={moment(educationalDetails.updateInfo.startedFrom).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setEducationalDetails({
												updateInfo: {
													...educationalDetails.updateInfo,
													startedFrom: moment(e.target.value).toDate(),
												},
											})
										}
									/>
								</div>
							</div>
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="endedAt"
										value="End date"
									/>
								</div>
								<div className="flex gap-[16px]">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="endedAt"
										name="endedAt"
										value={moment(educationalDetails.updateInfo.endedAt).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setEducationalDetails({
												updateInfo: {
													...educationalDetails.updateInfo,
													endedAt: moment(e.target.value).toDate() || '',
												},
											})
										}
									/>
								</div>
							</div>
						</div>
					</div>

					{dateError && <div className="text-xs font-bold text-red-500">{dateError}</div>}
				</Modal.Body>

				<Modal.Footer className="rounded-b-md border-t-0 dark:bg-[#18222D]">
					<div className="flex w-full justify-end">
						<div className="">
							<Button
								color="purplePlaced"
								className="bg-[#7777E7]"
								type="submit"
								disabled={
									!educationalDetails.updateInfo.degreeName ||
									!educationalDetails.updateInfo.collegeName ||
									!educationalDetails.updateInfo.startedFrom ||
									editEducationDetailsFn.isLoading ||
									createEducationDetailsFn.isLoading
								}
							>
								{isEditing ? (
									<>{false ? <Spinner size="sm" light={true} /> : 'Save'}</>
								) : (
									<>{false ? <Spinner size="sm" light={true} /> : 'Save'}</>
								)}
							</Button>
						</div>
					</div>
				</Modal.Footer>
			</form>
		</Modal>
	);
};

export default EditProfileEducationModal;
